// ============================================================================
// EDIT PROJECT AVATAR MODAL
// Allows editing all fields of a project-scoped avatar definition in-place.
// ============================================================================

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Loader2, Bot, ChevronDown, ChevronUp, Sparkles, EyeOff, RefreshCw, GitBranch } from 'lucide-react';
import { updateProjectAvatar, generateAvatarSystemPrompt, generateAvatarImage } from '../../services/avatarCatalogService';
import { AvatarDefinition, AvatarCategory, TTSProvider, CognitiveElement, AvatarCognitivePolicy } from '../../types';
import StringListEditor from '../common/StringListEditor';
import TaggedListEditor from '../common/TaggedListEditor';
import VoiceSelector from '../VoiceSelector';

const COLOR_PALETTE = [
  { primary: '#009688', secondary: '#E0F2F1', label: 'Teal' },
  { primary: '#4CAF50', secondary: '#E8F5E9', label: 'Green' },
  { primary: '#FF9800', secondary: '#FFF3E0', label: 'Amber' },
  { primary: '#3F51B5', secondary: '#E8EAF6', label: 'Indigo' },
  { primary: '#E91E63', secondary: '#FCE4EC', label: 'Pink' },
  { primary: '#00BCD4', secondary: '#E0F7FA', label: 'Cyan' },
  { primary: '#673AB7', secondary: '#EDE7F6', label: 'Deep Purple' },
  { primary: '#8BC34A', secondary: '#F1F8E9', label: 'Light Green' },
  { primary: '#FF5722', secondary: '#FBE9E7', label: 'Deep Orange' },
  { primary: '#03A9F4', secondary: '#E1F5FE', label: 'Light Blue' },
  { primary: '#2196F3', secondary: '#E3F2FD', label: 'Blue' },
  { primary: '#9C27B0', secondary: '#F3E5F5', label: 'Purple' },
  { primary: '#F44336', secondary: '#FFEBEE', label: 'Red' },
  { primary: '#607D8B', secondary: '#ECEFF1', label: 'Blue Grey' },
];

interface EditProjectAvatarModalProps {
  projectId: string;
  avatar: AvatarDefinition;
  onClose: () => void;
  onSaved: (updated: AvatarDefinition) => void;
}

interface AvatarImageSectionProps {
  name: string;
  generatingImage: boolean;
  previewImageUrl: string | null;
  pendingImageUrl: string | null;
  saving: boolean;
  onGenerate: () => void;
  onAccept: () => void;
}

const AvatarImageSection: React.FC<AvatarImageSectionProps> = ({ name, generatingImage, previewImageUrl, pendingImageUrl, saving, onGenerate, onAccept }) => {
  if (generatingImage) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '16px', border: '1px dashed #e2e8f0', borderRadius: '10px', color: '#64748b', fontSize: '13px' }}>
        <Loader2 size={16} style={{ animation: 'spin 1s linear infinite', flexShrink: 0 }} />
        Generating sketchbook portrait… this takes 15–30s
      </div>
    );
  }

  if (previewImageUrl) {
    return (
      <div>
        <img src={previewImageUrl} alt="Generated avatar preview" style={{ width: '100%', maxHeight: '200px', objectFit: 'cover', borderRadius: '10px', border: '1px solid #e2e8f0', display: 'block' }} />
        <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
          <button onClick={onAccept} style={{ flex: 1, padding: '7px', background: '#00c7c4', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>Accept</button>
          <button onClick={onGenerate} disabled={generatingImage} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', padding: '7px 14px', background: 'white', color: '#374151', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
            <RefreshCw size={12} /> Regen
          </button>
        </div>
      </div>
    );
  }

  if (pendingImageUrl) {
    return (
      <div>
        <img src={pendingImageUrl} alt="Avatar image" style={{ width: '100%', maxHeight: '200px', objectFit: 'cover', borderRadius: '10px', border: '1px solid #e2e8f0', display: 'block' }} />
        <button onClick={onGenerate} style={{ display: 'flex', alignItems: 'center', gap: '5px', marginTop: '6px', fontSize: '12px', fontWeight: '600', color: '#64748b', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 0' }}>
          <RefreshCw size={11} /> Regenerate image
        </button>
      </div>
    );
  }

  return (
    <button onClick={onGenerate} disabled={saving} style={{ width: '100%', padding: '20px', border: '1.5px dashed #e2e8f0', borderRadius: '10px', background: '#f8fafc', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', color: '#94a3b8' }}>
      <Bot size={24} color="#cbd5e1" />
      <span style={{ fontSize: '12px', fontWeight: '500' }}>Click to generate a sketchbook portrait</span>
    </button>
  );
};

interface UseEditAvatarFormResult {
  name: string;
  setName: (v: string) => void;
  description: string;
  setDescription: (v: string) => void;
  systemPrompt: string;
  setSystemPrompt: (v: string) => void;
  keywords: string;
  setKeywords: (v: string) => void;
  category: AvatarCategory;
  setCategory: (v: AvatarCategory) => void;
  ttsVoiceId: string;
  setTtsVoiceId: (v: string) => void;
  primaryColor: string;
  setPrimaryColor: (v: string) => void;
  secondaryColor: string;
  setSecondaryColor: (v: string) => void;
  isEnabled: boolean;
  toggleEnabled: () => void;
  showPrompt: boolean;
  toggleShowPrompt: () => void;
  cogGoals: string[];
  setCogGoals: (v: string[]) => void;
  cogLenses: CognitiveElement[];
  setCogLenses: (v: CognitiveElement[]) => void;
  cogMandatoryQuestions: CognitiveElement[];
  setCogMandatoryQuestions: (v: CognitiveElement[]) => void;
  cogEvidenceRules: CognitiveElement[];
  setCogEvidenceRules: (v: CognitiveElement[]) => void;
  cogDecisionCriteria: CognitiveElement[];
  setCogDecisionCriteria: (v: CognitiveElement[]) => void;
  showCognitivePolicy: boolean;
  toggleShowCognitivePolicy: () => void;
  saving: boolean;
  regenerating: boolean;
  generatingImage: boolean;
  previewImageUrl: string | null;
  pendingImageUrl: string | null;
  error: string;
  handleColorPreset: (primary: string, secondary: string) => void;
  handleGenerateImage: () => Promise<void>;
  handleAcceptImage: () => void;
  handleRegenPrompt: () => Promise<void>;
  handleSave: () => Promise<void>;
}

// ============================================================================
// FORM HOOK — owns all editable state for the modal plus its 5 handlers.
// Colocated in this file since it is a one-off, tightly-coupled form hook
// (not a reusable data hook like the query hooks in src/hooks/).
// ============================================================================
function useEditAvatarForm(
  projectId: string,
  avatar: AvatarDefinition,
  onSaved: (updated: AvatarDefinition) => void
): UseEditAvatarFormResult {
  const [name, setName] = useState(avatar.name);
  const [description, setDescription] = useState(avatar.description);
  const [systemPrompt, setSystemPrompt] = useState(avatar.systemPrompt || '');
  const [keywords, setKeywords] = useState((avatar.keywords || []).join(', '));
  const [category, setCategory] = useState(avatar.category || AvatarCategory.CUSTOM);
  const [ttsVoiceId, setTtsVoiceId] = useState(avatar.ttsVoiceId || '21m00Tcm4TlvDq8ikWAM');
  const [primaryColor, setPrimaryColor] = useState(avatar.primaryColor || '#009688');
  const [secondaryColor, setSecondaryColor] = useState(avatar.secondaryColor || '#E0F2F1');
  const [isEnabled, setIsEnabled] = useState(avatar.isEnabled !== false);
  const [showPrompt, setShowPrompt] = useState(false);
  const [cogGoals, setCogGoals] = useState<string[]>(avatar.cognitivePolicy?.goals || []);
  const [cogLenses, setCogLenses] = useState<CognitiveElement[]>(avatar.cognitivePolicy?.lenses || []);
  const [cogMandatoryQuestions, setCogMandatoryQuestions] = useState<CognitiveElement[]>(avatar.cognitivePolicy?.mandatoryQuestions || []);
  const [cogEvidenceRules, setCogEvidenceRules] = useState<CognitiveElement[]>(avatar.cognitivePolicy?.evidenceRules || []);
  const [cogDecisionCriteria, setCogDecisionCriteria] = useState<CognitiveElement[]>(avatar.cognitivePolicy?.decisionCriteria || []);
  const [showCognitivePolicy, setShowCognitivePolicy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [generatingImage, setGeneratingImage] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);
  const [pendingImageUrl, setPendingImageUrl] = useState<string | null>(avatar.imageUrl || null);
  const [pendingImageS3Key, setPendingImageS3Key] = useState<string | null>(null);
  const [error, setError] = useState('');

  const toggleEnabled = () => setIsEnabled(v => !v);
  const toggleShowPrompt = () => setShowPrompt(p => !p);
  const toggleShowCognitivePolicy = () => setShowCognitivePolicy(p => !p);

  const buildCognitivePolicy = (): AvatarCognitivePolicy | undefined => {
    const hasCognitiveContent = cogGoals.length > 0 || cogLenses.length > 0
      || cogMandatoryQuestions.length > 0 || cogEvidenceRules.length > 0 || cogDecisionCriteria.length > 0;
    if (!hasCognitiveContent) return undefined;
    return {
      goals: cogGoals,
      lenses: cogLenses,
      mandatoryQuestions: cogMandatoryQuestions,
      evidenceRules: cogEvidenceRules,
      decisionCriteria: cogDecisionCriteria
    };
  };

  const handleColorPreset = (primary: string, secondary: string) => {
    setPrimaryColor(primary);
    setSecondaryColor(secondary);
  };

  const handleGenerateImage = async () => {
    if (!name.trim() || !description.trim()) {
      setError('Name and description are required to generate an image.');
      return;
    }
    setError('');
    setGeneratingImage(true);
    setPreviewImageUrl(null);
    try {
      const result = await generateAvatarImage(projectId, avatar.avatarDefinitionId, name.trim(), description.trim());
      setPreviewImageUrl(result.imageUrl);
      setPendingImageS3Key(result.imageS3Key);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Image generation failed');
    } finally {
      setGeneratingImage(false);
    }
  };

  const handleAcceptImage = () => {
    if (previewImageUrl) {
      setPendingImageUrl(previewImageUrl);
      setPreviewImageUrl(null);
    }
  };

  const handleRegenPrompt = async () => {
    if (!name.trim() || !description.trim()) {
      setError('Name and description are required to regenerate.');
      return;
    }
    setError('');
    setRegenerating(true);
    try {
      const result = await generateAvatarSystemPrompt(projectId, name.trim(), description.trim());
      setSystemPrompt(result.systemPrompt);
      setKeywords(result.keywords.join(', '));
      setShowPrompt(true);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to regenerate prompt');
    } finally {
      setRegenerating(false);
    }
  };

  const buildImagePayload = (): { imageUrl?: string } => {
    if (pendingImageS3Key) return { imageUrl: pendingImageS3Key };
    const originalImageUrl = avatar.imageUrl ?? null;
    if (pendingImageUrl !== originalImageUrl) return { imageUrl: pendingImageUrl ?? undefined };
    return {};
  };

  const handleSave = async () => {
    setError('');

    const hasCognitiveContent = cogGoals.length > 0 || cogLenses.length > 0
      || cogMandatoryQuestions.length > 0 || cogEvidenceRules.length > 0 || cogDecisionCriteria.length > 0;
    if (hasCognitiveContent && cogGoals.length === 0) {
      setError('Cognitive policy requires at least one goal');
      return;
    }
    if (hasCognitiveContent && cogMandatoryQuestions.length === 0) {
      setError('Cognitive policy requires at least one mandatory question');
      return;
    }

    setSaving(true);
    try {
      const kwArray = keywords.split(',').map(k => k.trim()).filter(Boolean);
      const resolvedKeywords = kwArray.length > 0 ? kwArray : [name.trim().toLowerCase()];
      const result = await updateProjectAvatar(projectId, avatar.avatarDefinitionId, {
        name: name.trim(),
        description: description.trim(),
        systemPrompt: systemPrompt.trim(),
        keywords: resolvedKeywords,
        category,
        ttsProvider: TTSProvider.ELEVENLABS,
        ttsVoiceId,
        primaryColor,
        secondaryColor,
        isEnabled,
        cognitivePolicy: buildCognitivePolicy(),
        ...buildImagePayload()
      });
      onSaved(result.avatar);
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  return {
    name, setName,
    description, setDescription,
    systemPrompt, setSystemPrompt,
    keywords, setKeywords,
    category, setCategory,
    ttsVoiceId, setTtsVoiceId,
    primaryColor, setPrimaryColor,
    secondaryColor, setSecondaryColor,
    isEnabled, toggleEnabled,
    showPrompt, toggleShowPrompt,
    cogGoals, setCogGoals,
    cogLenses, setCogLenses,
    cogMandatoryQuestions, setCogMandatoryQuestions,
    cogEvidenceRules, setCogEvidenceRules,
    cogDecisionCriteria, setCogDecisionCriteria,
    showCognitivePolicy, toggleShowCognitivePolicy,
    saving,
    regenerating,
    generatingImage,
    previewImageUrl,
    pendingImageUrl,
    error,
    handleColorPreset,
    handleGenerateImage,
    handleAcceptImage,
    handleRegenPrompt,
    handleSave
  };
}

interface EnableToggleProps {
  isEnabled: boolean;
  onToggle: () => void;
}

const EnableToggle: React.FC<EnableToggleProps> = ({ isEnabled, onToggle }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderRadius: '10px', background: isEnabled ? '#f0fdf4' : '#f8fafc', border: `1px solid ${isEnabled ? '#bbf7d0' : '#e2e8f0'}` }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <EyeOff size={15} color={isEnabled ? '#16a34a' : '#94a3b8'} />
      <div>
        <p style={{ margin: 0, fontSize: '13px', fontWeight: '600', color: isEnabled ? '#15803d' : '#64748b' }}>
          {isEnabled ? 'Avatar enabled' : 'Avatar disabled'}
        </p>
        <p style={{ margin: 0, fontSize: '11px', color: '#94a3b8' }}>
          {isEnabled ? 'Included in new sessions for this project' : 'Hidden from new sessions — existing sessions unaffected'}
        </p>
      </div>
    </div>
    <button
      onClick={onToggle}
      style={{
        width: '44px', height: '24px', borderRadius: '12px', border: 'none', cursor: 'pointer',
        background: isEnabled ? '#16a34a' : '#cbd5e1', position: 'relative', transition: 'background 0.2s', flexShrink: 0
      }}
      aria-label={isEnabled ? 'Disable avatar' : 'Enable avatar'}
    >
      <span style={{
        position: 'absolute', top: '3px', left: isEnabled ? '23px' : '3px',
        width: '18px', height: '18px', borderRadius: '50%', background: 'white',
        transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
      }} />
    </button>
  </div>
);

export const EditProjectAvatarModal: React.FC<EditProjectAvatarModalProps> = ({
  projectId,
  avatar,
  onClose,
  onSaved
}) => {
  const {
    name, setName,
    description, setDescription,
    systemPrompt, setSystemPrompt,
    keywords, setKeywords,
    category, setCategory,
    ttsVoiceId, setTtsVoiceId,
    primaryColor, setPrimaryColor,
    secondaryColor, setSecondaryColor,
    isEnabled, toggleEnabled,
    showPrompt, toggleShowPrompt,
    cogGoals, setCogGoals,
    cogLenses, setCogLenses,
    cogMandatoryQuestions, setCogMandatoryQuestions,
    cogEvidenceRules, setCogEvidenceRules,
    cogDecisionCriteria, setCogDecisionCriteria,
    showCognitivePolicy, toggleShowCognitivePolicy,
    saving,
    regenerating,
    generatingImage,
    previewImageUrl,
    pendingImageUrl,
    error,
    handleColorPreset,
    handleGenerateImage,
    handleAcceptImage,
    handleRegenPrompt,
    handleSave
  } = useEditAvatarForm(projectId, avatar, onSaved);

  const navigate = useNavigate();

  const fieldLabel = (text: string) => (
    <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: '#374151', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
      {text}
    </label>
  );

  const input = (props: React.InputHTMLAttributes<HTMLInputElement>) => (
    <input
      {...props}
      style={{
        width: '100%', padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: '8px',
        fontSize: '14px', color: '#0f172a', background: 'white', boxSizing: 'border-box',
        ...(props.style || {})
      }}
    />
  );

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: '20px' }}
      onClick={onClose}
    >
      <div
        style={{ background: 'white', borderRadius: '14px', width: '100%', maxWidth: '600px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 64px rgba(0,0,0,0.22)', overflow: 'hidden' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ padding: '18px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ width: '36px', height: '36px', borderRadius: '8px', background: secondaryColor, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, overflow: 'hidden' }}>
              {pendingImageUrl
                ? <img src={pendingImageUrl} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                : <Bot size={20} color={primaryColor} />}
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: '#0f172a' }}>Edit Avatar</h2>
              <p style={{ margin: 0, fontSize: '12px', color: '#64748b' }}>{avatar.name}</p>
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', display: 'flex' }}>
            <X size={20} color="#94a3b8" />
          </button>
        </div>

        {/* Scrollable body */}
        <div style={{ padding: '20px 24px', overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: '16px' }}>

          {/* Avatar Image */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
              {fieldLabel('Avatar Image')}
              {!generatingImage && !previewImageUrl && (
                <button onClick={handleGenerateImage} disabled={saving} style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', fontWeight: '600', color: '#00c7c4', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 0' }}>
                  <Sparkles size={12} />
                  Generate with AI
                </button>
              )}
            </div>
            <AvatarImageSection name={name} generatingImage={generatingImage} previewImageUrl={previewImageUrl} pendingImageUrl={pendingImageUrl} saving={saving} onGenerate={handleGenerateImage} onAccept={handleAcceptImage} />
          </div>

          {/* Name + Description */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div>
              {fieldLabel('Name')}
              {input({ value: name, onChange: e => setName(e.target.value), placeholder: 'e.g. Sarah the Buyer' })}
            </div>
            <div>
              {fieldLabel('Category')}
              <select
                value={category}
                onChange={e => setCategory(e.target.value as AvatarCategory)}
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', color: '#0f172a', background: 'white', boxSizing: 'border-box' }}
              >
                {Object.values(AvatarCategory).map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            {fieldLabel('Description / Role')}
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              rows={2}
              style={{ width: '100%', padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', color: '#0f172a', resize: 'vertical', boxSizing: 'border-box' }}
            />
          </div>

          {/* System Prompt */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '5px' }}>
              {fieldLabel('System Prompt')}
              <button
                onClick={handleRegenPrompt}
                disabled={regenerating || saving}
                style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', fontWeight: '600', color: '#00c7c4', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 0' }}
              >
                {regenerating ? <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} /> : <Sparkles size={12} />}
                Regenerate with AI
              </button>
            </div>
            <div style={{ border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
              <button
                onClick={toggleShowPrompt}
                style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: '#f8fafc', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: '600', color: '#374151' }}
              >
                <span>System Prompt {showPrompt ? '(editing)' : '(click to expand)'}</span>
                {showPrompt ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </button>
              {showPrompt && (
                <textarea
                  value={systemPrompt}
                  onChange={e => setSystemPrompt(e.target.value)}
                  rows={6}
                  style={{ width: '100%', padding: '10px 12px', border: 'none', borderTop: '1px solid #e2e8f0', fontSize: '13px', color: '#334155', lineHeight: '1.6', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit' }}
                />
              )}
            </div>
          </div>

          {/* Cognitive Policy */}
          <div data-training-id="avatar-cognitive-policy">
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '10px', marginBottom: '8px' }}>
              <p style={{ margin: 0, fontSize: '12px', color: '#64748b', lineHeight: '1.5' }}>
                Beyond the system prompt above, optionally define how this avatar reasons: what
                it's trying to achieve, the perspectives it applies, what it must always raise,
                its evidence standards, and what it weighs when taking a position. Lenses,
                questions, evidence rules, and decision criteria can be tagged with topics — they
                only activate when the conversation is actually about that topic. Leave everything
                blank to keep this avatar on its system prompt alone.
              </p>
              {avatar.cognitivePolicy && (
                <button
                  type="button"
                  onClick={() => navigate(`/avatars/${avatar.avatarDefinitionId}/cognition-graph`)}
                  style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', fontWeight: '600', color: '#00c7c4', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 0', flexShrink: 0, whiteSpace: 'nowrap' }}
                >
                  <GitBranch size={12} />
                  View reasoning graph ↗
                </button>
              )}
            </div>
            <div style={{ border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
              <button
                onClick={toggleShowCognitivePolicy}
                style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: '#f8fafc', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: '600', color: '#374151' }}
              >
                <span>Cognitive Policy (optional) {showCognitivePolicy ? '(editing)' : '(click to expand)'}</span>
                {showCognitivePolicy ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </button>
              {showCognitivePolicy && (
                <div style={{ padding: '14px 12px', borderTop: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column', gap: '18px' }}>
                  <StringListEditor
                    label="Goals (what this avatar is trying to achieve)"
                    items={cogGoals}
                    onChange={setCogGoals}
                    placeholder="Enter a goal and press Enter..."
                    hint="Always active — not topic-gated."
                  />
                  <TaggedListEditor
                    label="Lenses (perspectives it habitually applies)"
                    items={cogLenses}
                    onChange={setCogLenses}
                    textPlaceholder="e.g., Reality-check technical complexity and timelines"
                  />
                  <TaggedListEditor
                    label="Mandatory Questions (must raise/consider before answering)"
                    items={cogMandatoryQuestions}
                    onChange={setCogMandatoryQuestions}
                    textPlaceholder="e.g., What fails independently?"
                  />
                  <TaggedListEditor
                    label="Evidence Rules"
                    items={cogEvidenceRules}
                    onChange={setCogEvidenceRules}
                    textPlaceholder="e.g., Don't invent evidence — cite the KB by name or label as an assumption"
                  />
                  <TaggedListEditor
                    label="Decision Criteria (what it weighs when taking a position)"
                    items={cogDecisionCriteria}
                    onChange={setCogDecisionCriteria}
                    textPlaceholder="e.g., Simplicity of the path to value"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Keywords */}
          <div>
            {fieldLabel('Keywords (comma-separated)')}
            {input({ value: keywords, onChange: e => setKeywords(e.target.value), placeholder: 'e.g. procurement, buyer, B2B' })}
          </div>

          {/* Color picker */}
          <div>
            {fieldLabel('Card Color')}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '10px' }}>
              {COLOR_PALETTE.map(c => (
                <button
                  key={c.primary}
                  title={c.label}
                  onClick={() => handleColorPreset(c.primary, c.secondary)}
                  style={{
                    width: '28px', height: '28px', borderRadius: '50%', background: c.primary, border: primaryColor === c.primary ? '3px solid #0f172a' : '2px solid transparent',
                    cursor: 'pointer', flexShrink: 0, transition: 'transform 0.1s', transform: primaryColor === c.primary ? 'scale(1.2)' : 'scale(1)'
                  }}
                />
              ))}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              <div>
                <p style={{ margin: '0 0 4px', fontSize: '11px', color: '#64748b', fontWeight: '500' }}>Primary (border/icon)</p>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input type="color" value={primaryColor} onChange={e => setPrimaryColor(e.target.value)} style={{ width: '36px', height: '36px', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '2px', cursor: 'pointer' }} />
                  {input({ value: primaryColor, onChange: e => setPrimaryColor(e.target.value), style: { flex: 1, fontFamily: 'monospace', fontSize: '13px' } })}
                </div>
              </div>
              <div>
                <p style={{ margin: '0 0 4px', fontSize: '11px', color: '#64748b', fontWeight: '500' }}>Secondary (background)</p>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <input type="color" value={secondaryColor} onChange={e => setSecondaryColor(e.target.value)} style={{ width: '36px', height: '36px', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '2px', cursor: 'pointer' }} />
                  {input({ value: secondaryColor, onChange: e => setSecondaryColor(e.target.value), style: { flex: 1, fontFamily: 'monospace', fontSize: '13px' } })}
                </div>
              </div>
            </div>
            {/* Preview */}
            <div style={{ marginTop: '10px', padding: '10px 14px', borderRadius: '10px', background: secondaryColor, border: `2px solid ${primaryColor}`, display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: secondaryColor, border: `2px solid ${primaryColor}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Bot size={16} color={primaryColor} />
              </div>
              <span style={{ fontSize: '13px', fontWeight: '600', color: primaryColor }}>{name || 'Avatar Name'}</span>
            </div>
          </div>

          {/* Voice */}
          <div data-training-id="avatar-voice-picker">
            {fieldLabel('Voice')}
            <VoiceSelector value={ttsVoiceId} onChange={setTtsVoiceId} disabled={saving} />
          </div>

          {/* Enable/Disable toggle */}
          <EnableToggle isEnabled={isEnabled} onToggle={toggleEnabled} />

          {error && (
            <p style={{ margin: 0, fontSize: '13px', color: '#ef4444', padding: '8px 12px', background: '#fef2f2', borderRadius: '8px' }}>{error}</p>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 24px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '10px', background: '#f8fafc', flexShrink: 0 }}>
          <button
            onClick={onClose}
            disabled={saving}
            style={{ padding: '9px 16px', background: 'white', color: '#374151', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer' }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '9px 18px', background: saving ? '#94a3b8' : '#00c7c4', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: saving ? 'not-allowed' : 'pointer' }}
          >
            {saving && <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />}
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditProjectAvatarModal;
